import type {FileReference, SnippetReference} from '@github-ui/copilot-chat-types'
import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import type {MouseEvent} from 'react'

import type {ReferenceActivation, ReferenceImage} from '../contexts/ReferenceActivationContext'

export interface UploadedReferenceSource {
  id?: string
  name: string
  contentType: string
  data?: string
  text?: string
  url?: string
}

export type ReferenceActivationTarget =
  | {kind: 'repository'; owner: string; name: string; id?: number; ref?: string; url: string}
  | {kind: 'file'; reference: FileReference | SnippetReference}
  | {kind: 'upload'; source: UploadedReferenceSource}
  | {kind: 'image'; image: ReferenceImage}
  | {kind: 'space'; url: string}

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

export function safeReferenceUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value) return undefined
  try {
    const url = new URL(value, ssrSafeLocation.origin || 'https://github.com')
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : undefined
  } catch {
    return undefined
  }
}

export function isPlainReferenceActivation(event?: MouseEvent): boolean {
  return (
    !event ||
    (!event.defaultPrevented &&
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Preserve native modified-link navigation.
      !event.metaKey &&
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Preserve native modified-link navigation.
      !event.ctrlKey &&
      !event.shiftKey &&
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Preserve native modified-link navigation.
      !event.altKey &&
      event.button === 0)
  )
}

export function messageAttachmentUrl(attachment: MessageAttachment): string | undefined {
  const detail = record(attachment._meta?.['copilot.attachmentDetail'])
  const target = normalizeMessageAttachment(attachment)
  if (target?.kind === 'repository' || target?.kind === 'space') return target.url
  if (target?.kind === 'file') return target.reference.url
  if (target?.kind === 'upload') return target.source.url
  if (target?.kind === 'image') return safeReferenceUrl(target.image.src)
  return safeReferenceUrl(detail?.url)
}

function normalizeMessageAttachment(attachment: MessageAttachment, id?: string): ReferenceActivationTarget | undefined {
  const detail = record(attachment._meta?.['copilot.attachmentDetail'])
  const repo = record(detail?.repo)
  const owner = text(repo?.owner)
  const name = text(repo?.name)
  const repoID = typeof repo?.id === 'number' && Number.isSafeInteger(repo.id) ? repo.id : undefined
  if (detail?.type === 'github_repository' && owner && name) {
    return {
      kind: 'repository',
      owner,
      name,
      id: repoID,
      ref: text(detail.ref),
      url:
        safeReferenceUrl(detail.url) ??
        `${ssrSafeLocation.origin || 'https://github.com'}/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`,
    }
  }
  if ((detail?.type === 'github_file' || detail?.type === 'github_snippet') && owner && name) {
    const path = text(detail.path)
    const submittedRef = text(detail.ref)
    if (!path || !submittedRef) return undefined
    const ui = record(attachment._meta?.['github.attachmentUi'])
    const pinnedCommit = text(ui?.commitOID)
    const hasPinnedRevision = pinnedCommit === submittedRef
    const ref = (hasPinnedRevision && text(ui?.ref)) || submittedRef
    const commitOID = (hasPinnedRevision ? pinnedCommit : text(detail.commitOID)) ?? ''
    const url =
      safeReferenceUrl(detail.url) ??
      `${ssrSafeLocation.origin || 'https://github.com'}/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/blob/${encodeURIComponent(commitOID || ref)}/${path.split('/').map(encodeURIComponent).join('/')}`
    const reference: FileReference = {
      type: 'file',
      repoOwner: owner,
      repoName: name,
      repoID: repoID ?? 0,
      path,
      ref,
      commitOID,
      url,
    }
    const range = record(detail.lineRange)
    if (
      detail.type === 'github_snippet' &&
      typeof range?.start === 'number' &&
      typeof range.end === 'number' &&
      Number.isSafeInteger(range.start) &&
      Number.isSafeInteger(range.end) &&
      range.start > 0 &&
      range.end >= range.start
    ) {
      return {kind: 'file', reference: {...reference, type: 'snippet', range: {start: range.start, end: range.end}}}
    }
    return {kind: 'file', reference}
  }
  if (attachment.type === 'embeddedResource') {
    const contentType = text(attachment.contentType) ?? 'application/octet-stream'
    const data = 'data' in attachment ? text(attachment.data) : undefined
    const content = 'text' in attachment ? text(attachment.text) : undefined
    const sourceId = text(attachment._meta?.['github.attachmentId']) ?? id
    if (contentType.startsWith('image/') && data) {
      return {
        kind: 'image',
        image: {
          id: sourceId,
          name: attachment.label || 'Image',
          src: `data:${contentType};base64,${data}`,
          contentType,
        },
      }
    }
    return {
      kind: 'upload',
      source: {id: sourceId, name: attachment.label || 'Attachment', contentType, data, text: content},
    }
  }
  if (attachment.type === 'resource') {
    const url = safeReferenceUrl(attachment.uri)
    const contentType = attachment.contentType ?? 'application/octet-stream'
    const sourceId = id ?? attachment.uri
    if (contentType.startsWith('image/') && url) {
      return {kind: 'image', image: {id: sourceId, name: attachment.label || 'Image', src: url, contentType}}
    }
    return {kind: 'upload', source: {id: sourceId, name: attachment.label || 'Attachment', contentType, url}}
  }
  return undefined
}

export function normalizeReferenceActivation(activation: ReferenceActivation): ReferenceActivationTarget | undefined {
  switch (activation.kind) {
    case 'attachment':
      return normalizeMessageAttachment(activation.attachment, activation.id)
    case 'image':
      return {kind: 'image', image: activation.image}
    case 'space':
      return {kind: 'space', url: `/copilot/spaces/${encodeURIComponent(activation.owner)}/${activation.number}`}
    case 'reference': {
      const reference = activation.reference
      switch (reference.type) {
        case 'repository':
          return {
            kind: 'repository',
            owner: reference.ownerLogin,
            name: reference.name,
            id: reference.id,
            ref: reference.ref,
            url: `${ssrSafeLocation.origin || 'https://github.com'}/${encodeURIComponent(reference.ownerLogin)}/${encodeURIComponent(reference.name)}`,
          }
        case 'file':
        case 'file-v2':
        case 'snippet':
          return {kind: 'file', reference}
        case 'image':
          return reference.imageUrl
            ? {
                kind: 'image',
                image: {
                  id: reference.id,
                  name: reference.name,
                  src: reference.imageUrl,
                  contentType: reference.mediaType,
                },
              }
            : undefined
        case 'thread-scoped-file':
          return {kind: 'upload', source: {name: reference.name, text: reference.text, contentType: 'text/plain'}}
        case 'thread-scoped-snippet':
          return {
            kind: 'upload',
            source: {name: reference.name, text: reference.snippetText, contentType: 'text/plain'},
          }
        default:
          return undefined
      }
    }
  }
}
